import React from 'react'
import { Link } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { Dropdown, Tooltip } from 'antd'
import { CheckCircleOutlined, DeleteOutlined, EditOutlined, EllipsisOutlined, MailOutlined, StopOutlined, SwapOutlined, UserOutlined } from '@ant-design/icons'
import { PlatformEnum } from '@vacationtracker/shared/data/platforms'
import { EmailInviteStatusEnum } from '@vacationtracker/shared/types/user'

import IntlMessages from '../../../../util/IntlMessages'
import { IUserFilteredInfo } from '../../../../types/users'

export type RowMenuActionKey = 'editUser' | 'inviteUser' | 'deactivateUser' | 'deleteUser' | 'activateUser'

export interface IRowActionsMenuProps {
  user: IUserFilteredInfo
  companyPlatform?: string
  userPlatform?: string
  isMicrosoftBillingPayment: boolean
  canManageUser: (row: IUserFilteredInfo) => boolean
  getInviteStatus: (user: IUserFilteredInfo) => EmailInviteStatusEnum
  isInBulkSelection?: (userId: string) => boolean
  onAction: (key: RowMenuActionKey, user: IUserFilteredInfo) => void
  /**
   * Set on a location's own Users tab, where the menu narrows to view / move / deactivate. Moving is
   * the only way to change location there, since that column is hidden. Role-locked entries are
   * shown disabled with the reason rather than dropped, so the menu reads the same for everyone.
   */
  locationMove?: {
    options: { id: string; name: string }[]
    /** Asked per action: a location admin may deactivate someone here but not move them elsewhere. */
    getLockReason: (user: IUserFilteredInfo, action: 'move' | 'status') => string | undefined
    onMove: (user: IUserFilteredInfo, locationId: string) => void
  }
}

const MOVE_KEY_PREFIX = 'move:'

export const RowActionsMenu = (props: IRowActionsMenuProps): React.ReactElement => {
  const { user, companyPlatform, userPlatform, isMicrosoftBillingPayment, canManageUser, getInviteStatus, isInBulkSelection, onAction, locationMove } = props
  const { formatMessage } = useIntl()
  const id = user.id

  // A real button, not a bare icon: the menu floats over the row rather than sitting in a column
  // of its own, so it has to be reachable by keyboard and named for whose row it acts on.
  const trigger = (
    <button type="button" className="vt-users-row-menu-trigger" aria-label={formatMessage({ id: 'users.rowMenu.label' }, { name: user.name })}>
      <EllipsisOutlined />
    </button>
  )

  const viewProfileItem = {
    key: 'view',
    icon: <UserOutlined />,
    label: (
      <Link to={`/app/users/${id}`}>
        <IntlMessages id="users.table.actionsViewProfile" />
      </Link>
    ),
  }

  const canEdit = canManageUser(user)
  const statusLockedBySelection = Boolean(isInBulkSelection?.(id))

  if (locationMove) {
    const moveLock = locationMove.getLockReason(user, 'move')
    const statusLock = locationMove.getLockReason(user, 'status')
    const withReason = (reason: string | undefined, label: React.ReactNode) => (reason ? <Tooltip title={<IntlMessages id={reason} />}>{label}</Tooltip> : label)
    const statusItem =
      user.status === 'ACTIVE'
        ? { key: 'deactivateUser' as const, icon: <StopOutlined />, label: <IntlMessages id="users.deactivate" /> }
        : { key: 'activateUser' as const, icon: <CheckCircleOutlined />, label: <IntlMessages id="app.activate" /> }

    return (
      <Dropdown
        menu={{
          items: [
            viewProfileItem,
            {
              key: 'move',
              icon: <SwapOutlined />,
              label: withReason(
                moveLock,
                <span>
                  <IntlMessages id="locationGeneral.moveTo" />
                </span>
              ),
              disabled: Boolean(moveLock) || locationMove.options.length === 0,
              children: locationMove.options.map((option) => ({ key: `${MOVE_KEY_PREFIX}${option.id}`, label: option.name })),
            },
            isMicrosoftBillingPayment || user.status === 'DELETED'
              ? null
              : { ...statusItem, label: withReason(statusLock, <span>{statusItem.label}</span>), disabled: Boolean(statusLock) || statusLockedBySelection },
          ].filter((item) => item !== null),
          onClick: (event) => {
            if (event.key.startsWith(MOVE_KEY_PREFIX)) {
              locationMove.onMove(user, event.key.slice(MOVE_KEY_PREFIX.length))
              return
            }
            if (event.key !== 'view') {
              onAction(event.key as RowMenuActionKey, user)
            }
          },
        }}
        trigger={['click']}
        placement="bottomRight"
      >
        {trigger}
      </Dropdown>
    )
  }

  if (!canEdit) {
    return (
      <Dropdown menu={{ items: [viewProfileItem] }} trigger={['click']} placement="bottomRight">
        {trigger}
      </Dropdown>
    )
  }

  const inviteStatus = getInviteStatus(user)
  const canInvite =
    !isMicrosoftBillingPayment && companyPlatform === PlatformEnum.Email && user.status === 'ACTIVE' && Boolean(user.isGhostUser) && inviteStatus !== EmailInviteStatusEnum.pending

  return (
    <Dropdown
      menu={{
        items: [
          viewProfileItem,
          {
            key: 'editUser',
            icon: <EditOutlined />,
            label: <IntlMessages id="users.table.actionsEditUser" />,
          },
          !isMicrosoftBillingPayment && user.status === 'ACTIVE'
            ? {
                key: 'deactivateUser',
                icon: <StopOutlined />,
                label: <IntlMessages id="users.deactivate" />,
                disabled: statusLockedBySelection,
              }
            : null,
          !isMicrosoftBillingPayment && user.status === 'INACTIVE'
            ? {
                key: 'activateUser',
                icon: <CheckCircleOutlined />,
                label: <IntlMessages id="app.activate" />,
                disabled: statusLockedBySelection,
              }
            : null,
          !isMicrosoftBillingPayment && user.status === 'INACTIVE' && userPlatform === 'email'
            ? {
                key: 'deleteUser',
                icon: <DeleteOutlined />,
                label: <IntlMessages id="app.deleteUser" />,
              }
            : null,
          canInvite
            ? {
                key: 'inviteUser',
                icon: <MailOutlined />,
                label: <IntlMessages id={inviteStatus === EmailInviteStatusEnum.expired ? 'users.reinvite' : 'app.invite'} />,
              }
            : null,
        ].filter((item) => item !== null),
        onClick: (event) => onAction(event.key as RowMenuActionKey, user),
      }}
      trigger={['click']}
      placement="bottomRight"
    >
      {trigger}
    </Dropdown>
  )
}
